import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CustomItemGroupModel, CustomItemGroupRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['customItemGroups']

type CustomItemGroupFilters = {
  agency_id?: number,
  string?: string,
}

type UseCustomItemGroupsOptions = {
  filters?: CustomItemGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: CustomItemGroupRequestOptions,
}

function useCustomItemGroups(options: UseCustomItemGroupsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { customItemGroups: customItemGroupsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { customItemGroups } = useSelector(reduxState => reduxState.entities)

  const filteredCustomItemGroups = useMemo(() => {
    const filtered = Object.values(customItemGroups).filter((customItemGroup: CustomItemGroupModel) => {
      const { agency_id, id, title } = customItemGroup

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchAgency && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [customItemGroupsUpdatedAt, JSON.stringify(filters)])

  const filteredCustomItemGroupsCount = filteredCustomItemGroups.length
  const hasFilteredCustomItemGroups = !!filteredCustomItemGroupsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCustomItemGroupsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCustomItemGroups } = useReduxAction(
    'customItemGroups',
    'loadCustomItemGroups',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCustomItemGroups,
    filteredCustomItemGroupsCount,
    hasFilteredCustomItemGroups,
    loading: loadingCustomItemGroups,
  }
}

export default useCustomItemGroups
